/**
 * Client-side registered-user activity logging.
 * Sends events to POST /api/users/activity when a JWT is present.
 * Complements GA (anonymous); this is for admin user timelines.
 */

const ACTIVITY_URL = '/api/users/activity';
const THROTTLE_MS = 30_000;

/** @type {Map<string, number>} */
const lastSent = new Map();

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) return null;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * @param {string} eventType
 * @param {{ path?: string, meta?: object, throttleKey?: string, throttleMs?: number }} [opts]
 */
export function logRegisteredUserActivity(eventType, opts = {}) {
  const headers = authHeaders();
  if (!headers) return;

  const type = String(eventType || '').trim().toLowerCase();
  if (!type) return;

  const path = opts.path != null ? String(opts.path) : undefined;
  const throttleKey = opts.throttleKey || `${type}:${path || ''}`;
  const throttleMs = opts.throttleMs ?? THROTTLE_MS;
  const now = Date.now();
  const prev = lastSent.get(throttleKey) || 0;
  if (throttleMs > 0 && now - prev < throttleMs) return;
  lastSent.set(throttleKey, now);

  const body = {
    eventType: type,
    ...(path != null ? { path } : {}),
    ...(opts.meta != null ? { meta: opts.meta } : {}),
  };

  // Fire-and-forget; never block UI
  try {
    fetch(ACTIVITY_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
}

export function logPageViewActivity(path, title) {
  logRegisteredUserActivity('page_view', {
    path,
    meta: title ? { title } : undefined,
    throttleKey: `page_view:${path}`,
  });
}

export function logProductViewActivity(product) {
  if (!product) return;
  const id = product.id ?? product.permalink;
  logRegisteredUserActivity('product_view', {
    path: id != null ? `/product/${id}` : undefined,
    meta: {
      productId: product.id,
      name: product.name,
      category: product.category || null,
    },
    throttleKey: `product_view:${id}`,
    throttleMs: 60_000,
  });
}

export function logAddToCartActivity(product, quantity = 1) {
  if (!product) return;
  logRegisteredUserActivity('add_to_cart', {
    path: product.id != null ? `/product/${product.id}` : undefined,
    meta: {
      productId: product.id,
      name: product.name,
      quantity,
    },
    throttleKey: `add_to_cart:${product.id}:${Date.now()}`,
    throttleMs: 0,
  });
}

export function logSearchActivity(query, path = '/search') {
  const q = String(query || '').trim();
  if (!q) return;
  logRegisteredUserActivity('search', {
    path,
    meta: { query: q.slice(0, 200) },
    throttleKey: `search:${q.toLowerCase()}`,
    throttleMs: 10_000,
  });
}

export default logRegisteredUserActivity;
